/**
 * Shared effect dispatcher.
 *
 * Both Counter rules and Mechanics describe their payload as a list of
 * EffectRow objects. This module turns one row into either:
 *   - a queued pending buff (next-* targets), or
 *   - a direct mutation of the live combat slot / played-instance value
 *     (this-* and popup-* targets).
 *
 * The output is a `MechanicResult`-shaped object so callers can fold it
 * through the engine's existing `applyResults` plumbing.
 */
import type {
  EffectRow,
  EffectKind,
  EffectTarget,
  EffectDirection,
} from "@/types/card";
import type {
  Buff,
  BuffEffect,
  BuffTrigger,
  EquipSlot,
  PendingPrompt,
  PlayerState,
} from "@/types/game";
import { uid } from "@/lib/utils";

// Result shape compatible with engine `applyResults`.
export interface EffectResult {
  selfPatch: Partial<PlayerState>;
  opponentPatch: Partial<PlayerState>;
  log: string[];
  /** Optional pendingPrompt to set on game state — used by loadout effects
   *  that need the player to pick weapon slots at play time. */
  prompt?: PendingPrompt;
}

export interface EffectCtx {
  self: PlayerState;
  opponent: PlayerState;
  cardName: string;
  currentRound: number;
  /** The player number who is the source of the effect (the one playing
   *  the card). Required for loadout effects that need to set a
   *  PendingPrompt with the correct `playerNum`. Optional for backwards
   *  compat with older callers — defaults to 1 when missing. */
  selfNum?: 1 | 2;
}

export function emptyResult(): EffectResult {
  return { selfPatch: {}, opponentPatch: {}, log: [] };
}

/** Apply a single EffectRow to the running result. Mutates `result` in place
 *  (it's a working accumulator). */
export function applyEffectRow(
  row: EffectRow,
  ctx: EffectCtx,
  result: EffectResult,
): void {
  // Validate direction/effect combo. If the editor lets a bad row through,
  // we silently drop it rather than crash a live game.
  if (!isValidCombo(row)) return;

  // Stamina effect: instant player-level mutation. Target picks the affected
  // player. buff-self = +amount, debuff-opponent = −amount.
  if (row.effect === "stamina") {
    applyStaminaEffect(row, ctx, result);
    return;
  }

  // Loadout effects — mutate armor wear counters or weapon equip slots.
  if (
    row.effect === "add-wear" ||
    row.effect === "remove-wear" ||
    row.effect === "exhaust" ||
    row.effect === "ready"
  ) {
    applyLoadoutEffect(row, ctx, result);
    return;
  }

  const isNext = row.target.startsWith("my-next-");
  if (isNext) {
    applyNextBuff(row, ctx, result);
    return;
  }
  // `this-*` / `popup-*` targets aren't queued buffs — they need ZoneCard /
  // CombatSlot / adValueOverride mutations that live OUTSIDE PlayerState.
  // Engine wiring (playtestEngine.ts) handles those by calling the inline
  // helpers `applyThisEffect` / `applyPopupEffect` directly. Effect rows that
  // hit this path here are no-ops at the PlayerState level — they're picked
  // up later by the slot-aware code path.
}

/** Some EffectRow combos make no sense (e.g. "buff-self / -power"). The form
 *  blocks them, but in case a legacy migration produced one, screen here. */
export function isValidCombo(row: EffectRow): boolean {
  if (row.direction === "buff-self") {
    if (
      row.effect === "remove-keyword" ||
      row.effect === "remove-power-buffs" ||
      row.effect === "remove-block-buffs"
    ) {
      return false;
    }
  }
  if (row.direction === "debuff-opponent") {
    if (row.effect === "add-keyword") {
      return false;
    }
  }
  // popup-* + stamina-cost: cost already paid.
  if (row.target.startsWith("popup-") && row.effect === "stamina-cost") {
    return false;
  }
  // stamina effect: must use self/opponent target (player-level mutation).
  if (row.effect === "stamina") {
    if (row.target !== "self" && row.target !== "opponent") return false;
    // self pairs with buff-self (gain); opponent pairs with debuff-opponent (lose).
    if (row.target === "self" && row.direction !== "buff-self") return false;
    if (row.target === "opponent" && row.direction !== "debuff-opponent") {
      return false;
    }
  } else if (row.effect === "draw") {
    // draw effect: same shape as stamina — self/opponent target only.
    // Routed directly by the engine (drawCards), not via this dispatcher.
    if (row.target !== "self" && row.target !== "opponent") return false;
    if (row.target === "self" && row.direction !== "buff-self") return false;
    if (row.target === "opponent" && row.direction !== "debuff-opponent") {
      return false;
    }
  } else if (row.effect === "search-deck") {
    // search-deck effect: player-level only (self target, buff-self direction).
    if (row.target !== "self") return false;
    if (row.direction !== "buff-self") return false;
  } else if (row.effect === "clear-stun") {
    // clear-stun: self target only. Used by Crimson Tempest to negate stun.
    if (row.target !== "self") return false;
    if (row.direction !== "buff-self") return false;
  } else if (
    row.effect === "add-wear" ||
    row.effect === "remove-wear"
  ) {
    // Wear effects: armor target only.
    if (row.target !== "my-armor" && row.target !== "opp-armor") return false;
    // my-armor pairs with loadout-self; opp-armor pairs with loadout-opponent.
    if (row.target === "my-armor" && row.direction !== "loadout-self") {
      return false;
    }
    if (
      row.target === "opp-armor" &&
      row.direction !== "loadout-opponent"
    ) {
      return false;
    }
  } else if (row.effect === "exhaust" || row.effect === "ready") {
    // Exhaust/ready: weapon target only.
    if (row.target !== "my-weapons" && row.target !== "opp-weapons") {
      return false;
    }
    if (row.target === "my-weapons" && row.direction !== "loadout-self") {
      return false;
    }
    if (
      row.target === "opp-weapons" &&
      row.direction !== "loadout-opponent"
    ) {
      return false;
    }
  } else {
    // Non-stamina/non-loadout effects must NOT use self/opponent or loadout
    // targets, and must NOT use loadout-* directions.
    if (
      row.target === "self" ||
      row.target === "opponent" ||
      row.target === "my-armor" ||
      row.target === "opp-armor" ||
      row.target === "my-weapons" ||
      row.target === "opp-weapons" ||
      row.target === "my-sigs" ||
      row.target === "opp-sigs" ||
      row.target === "my-champion" ||
      row.target === "opp-champion"
    ) {
      return false;
    }
    if (
      row.direction === "loadout-self" ||
      row.direction === "loadout-opponent"
    ) {
      return false;
    }
  }
  return true;
}

// ─── Stamina effect: instant player-level mutation ──────────────────────────

function applyStaminaEffect(
  row: EffectRow,
  ctx: EffectCtx,
  result: EffectResult,
): void {
  const amt = row.amount ?? 0;
  const sign = row.direction === "buff-self" ? 1 : -1;
  const delta = amt * sign;
  if (delta === 0) return;

  // target=self → mutate the playing player; target=opponent → mutate the other.
  const isSelf = row.target === "self";
  const patchKey: "selfPatch" | "opponentPatch" = isSelf
    ? "selfPatch"
    : "opponentPatch";
  const ownerState = isSelf ? ctx.self : ctx.opponent;

  const current =
    (result[patchKey].stamina as number | undefined) ?? ownerState.stamina;
  const max = ownerState.maxStamina;
  const next = Math.max(0, Math.min(max, current + delta));
  result[patchKey].stamina = next;

  const ownerLabel = isSelf ? "you" : "opponent";
  const verb = delta > 0 ? "gain" : "lose";
  result.log.push(
    `${ctx.cardName}: ${ownerLabel} ${verb} ${Math.abs(delta)} stamina`,
  );
}

// ─── Loadout effects: armor wear / weapon exhaust / weapon ready ─────────────

function applyLoadoutEffect(
  row: EffectRow,
  ctx: EffectCtx,
  result: EffectResult,
): void {
  // Owner = whose loadout we touch. my-* targets → the playing player;
  // opp-* targets → the opponent.
  const targetIsMine =
    row.target === "my-armor" ||
    row.target === "my-weapons" ||
    row.target === "my-sigs" ||
    row.target === "my-champion";
  const patchKey: "selfPatch" | "opponentPatch" = targetIsMine
    ? "selfPatch"
    : "opponentPatch";
  const ownerState = targetIsMine ? ctx.self : ctx.opponent;
  const ownerLabel = targetIsMine ? "your" : "opponent's";

  if (row.effect === "add-wear" || row.effect === "remove-wear") {
    const amt = row.amount ?? 0;
    if (amt === 0) return;
    const sign = row.effect === "add-wear" ? 1 : -1;
    const current =
      (result[patchKey].armorWearCounters as number | undefined) ??
      ownerState.armorWearCounters;
    const next = Math.max(0, current + amt * sign);
    result[patchKey].armorWearCounters = next;
    const verb = sign > 0 ? "gains" : "loses";
    result.log.push(
      `${ctx.cardName}: ${ownerLabel} armor ${verb} ${amt} wear counter${amt === 1 ? "" : "s"} (now ${next})`,
    );
    return;
  }

  if (row.effect === "exhaust" || row.effect === "ready") {
    const requested = Math.max(0, row.amount ?? 0);
    if (requested === 0) return;

    // Eligible slots: only those with a card AND in the right tap state.
    const slots = ownerState.equipSlots ?? [];
    const targetTapped = row.effect === "exhaust";
    const candidateSlots: number[] = [];
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      if (!s) continue;
      if (!s.cardId) continue;
      // For exhaust, only currently-untapped weapons; for ready, only tapped.
      if (targetTapped && s.tapped) continue;
      if (!targetTapped && !s.tapped) continue;
      candidateSlots.push(i);
    }

    if (candidateSlots.length === 0) {
      result.log.push(
        `${ctx.cardName}: no weapons eligible to ${row.effect}`,
      );
      return;
    }

    // If the player has exactly as many (or fewer) candidates as requested,
    // auto-apply to all of them — no prompt needed.
    if (candidateSlots.length <= requested) {
      const newSlots: EquipSlot[] = slots.map((s, i) =>
        candidateSlots.includes(i) ? { ...s, tapped: targetTapped } : s,
      );
      result[patchKey].equipSlots = newSlots;
      const verb = targetTapped ? "exhausted" : "readied";
      result.log.push(
        `${ctx.cardName}: ${verb} ${candidateSlots.length} ${ownerLabel} weapon${candidateSlots.length === 1 ? "" : "s"}`,
      );
      return;
    }

    // More candidates than requested — raise a prompt for the source player
    // (the one playing the card) to pick `requested` slots.
    const sourceNum: 1 | 2 = ctx.selfNum ?? 1;
    const ownerNum: 1 | 2 = targetIsMine ? sourceNum : (sourceNum === 1 ? 2 : 1);

    // Only set the prompt if one isn't already queued. The engine merges
    // results sequentially so the first loadout effect wins; subsequent
    // weapon-pick effects on the same card are dropped (designer should
    // batch). Log a notice in that case.
    if (!result.prompt) {
      result.prompt = {
        kind: "pick-loadout-weapons",
        playerNum: sourceNum,
        ownerNum,
        effect: row.effect,
        count: requested,
        candidateSlots,
        selectedSlots: [],
        cardName: ctx.cardName,
      };
      result.log.push(
        `${ctx.cardName}: pick ${requested} ${ownerLabel} weapon${requested === 1 ? "" : "s"} to ${row.effect}`,
      );
    } else {
      result.log.push(
        `${ctx.cardName}: skipped extra weapon pick (one already queued this play)`,
      );
    }
    return;
  }
}

// ─── Buff queueing (my-next-* / debuff-opponent-next) ─────────────────────

function applyNextBuff(
  row: EffectRow,
  ctx: EffectCtx,
  result: EffectResult,
): void {
  const trigger = nextTargetToBuffTrigger(row.target);
  const buff = buildBuff(row, trigger, ctx);
  if (!buff) {
    // remove-* / strip-buffs effects are not buffs themselves — they mutate
    // the target player's pendingBuffs list directly.
    applyStripEffect(row, ctx, result);
    return;
  }

  if (row.direction === "buff-self") {
    const existing =
      (result.selfPatch.pendingBuffs as Buff[] | undefined) ??
      ctx.self.pendingBuffs;
    result.selfPatch.pendingBuffs = [...existing, buff];
    result.log.push(buffLogLine(row, ctx));
    return;
  }
  // debuff-opponent
  const existing =
    (result.opponentPatch.pendingBuffs as Buff[] | undefined) ??
    ctx.opponent.pendingBuffs;
  result.opponentPatch.pendingBuffs = [...existing, buff];
  result.log.push(buffLogLine(row, ctx));
}

function nextTargetToBuffTrigger(t: EffectTarget): BuffTrigger {
  switch (t) {
    case "my-next-attack":
      return "next-attack";
    case "my-next-defend":
      return "next-defend";
    case "my-next-action":
      return "next-action";
    default:
      return "next-action";
  }
}

/** Build a Buff for stat/keyword/cost effects. Returns null for strip-style
 *  effects (which mutate the target buff list instead of adding a buff). */
function buildBuff(
  row: EffectRow,
  trigger: BuffTrigger,
  ctx: EffectCtx,
): Buff | null {
  const effect = effectRowToBuffEffect(row);
  if (!effect) return null;
  return {
    id: uid("buff_"),
    sourceCardName: ctx.cardName,
    trigger,
    effect,
    createdRound: ctx.currentRound,
    expiresRound: ctx.currentRound + 1,
    persistRound: true,
  };
}

/** Map an EffectRow into the engine's BuffEffect shape. The engine's
 *  firePendingBuffs / instanceValue look at `kind` to decide what changes. */
export function effectRowToBuffEffect(row: EffectRow): BuffEffect | null {
  // Sign of amount: positive for buff-self, negative for debuff-opponent.
  const sign = row.direction === "buff-self" ? 1 : -1;

  switch (row.effect) {
    case "power":
      return { kind: "power-bonus", amount: (row.amount ?? 0) * sign };
    case "block":
      return { kind: "block-bonus", amount: (row.amount ?? 0) * sign };
    case "stamina-cost":
      // buff-self → reduce cost (positive amount), debuff-opp → add cost.
      // We model both as `cost-reduction` with a signed amount — the engine's
      // `costReductionFromFired` sums amounts, so a negative value increases
      // the cost.
      return { kind: "cost-reduction", amount: (row.amount ?? 0) * sign };
    case "add-keyword": {
      const kind = keywordToGainKind(row.keyword ?? "");
      // Prefer the structured keywordAmount; fall back to a number embedded
      // in the chip text (legacy "Reflect 2" form).
      const amt = row.keywordAmount ?? parseKwAmount(row.keyword);
      // Reflect reads `power`; Disarm reads `count`. Populate both so legacy
      // consumers and the granted-keyword renderer (keywordGrantsFromFired)
      // see the same value regardless of which field they read.
      const eff: BuffEffect = { kind };
      if (typeof amt === "number") {
        if (kind === "gain-disarm") eff.count = amt;
        else eff.power = amt;
      }
      return eff;
    }
    case "remove-keyword":
    case "remove-power-buffs":
    case "remove-block-buffs":
      return null; // strip handled separately
    case "stamina":
      // Stamina is an instant player-level mutation, not a queued buff.
      // Handled by applyStaminaEffect before reaching this path.
      return null;
    case "add-wear":
    case "remove-wear":
    case "exhaust":
    case "ready":
      // Loadout effects — instant patches, not queued buffs.
      // Handled by applyLoadoutEffect before reaching this path.
      return null;
    case "draw":
      // Draw is an instant deck mutation, not a queued buff. The engine's
      // applyCounterEffectRow routes "draw" directly through drawCards()
      // before reaching the buff dispatcher.
      return null;
    case "search-deck":
      // search-deck is an instant prompt, not a queued buff.
      return null;
    case "clear-stun":
      // clear-stun is an instant state mutation, not a queued buff.
      // Handled by applyCounterEffectRow which clears stunned + wasStunnedThisRound.
      return null;
  }
}

function keywordToGainKind(kw: string): string {
  const k = kw.toLowerCase().split(/\s+/)[0] ?? "";
  switch (k) {
    case "stun":
      return "gain-stun";
    case "quick":
      return "gain-quick";
    case "piercing":
      return "gain-piercing";
    case "reflect":
      return "gain-reflect";
    case "chain":
      return "gain-chain";
    case "disarm":
      return "gain-disarm";
    case "crush":
      return "gain-crush";
    case "displace":
      return "gain-displace";
    default:
      return `gain-${k}`;
  }
}

function parseKwAmount(kw: string | undefined): number | undefined {
  if (!kw) return undefined;
  const m = kw.match(/(\d+)/);
  return m ? Number(m[1]) : undefined;
}

// ─── Strip effects: remove-power-buffs / remove-block-buffs / remove-keyword ─

function applyStripEffect(
  row: EffectRow,
  ctx: EffectCtx,
  result: EffectResult,
): void {
  // Direction governs whose buff list gets stripped: debuff-opponent strips
  // the OPPONENT's pendingBuffs (the most common case); buff-self stripping
  // is blocked at validation time for the remove-* effects.
  const ownerKey: "selfPatch" | "opponentPatch" =
    row.direction === "debuff-opponent" ? "opponentPatch" : "selfPatch";
  const owner = row.direction === "debuff-opponent" ? ctx.opponent : ctx.self;

  const existing =
    (result[ownerKey].pendingBuffs as Buff[] | undefined) ?? owner.pendingBuffs;

  let next: Buff[];
  switch (row.effect) {
    case "remove-power-buffs":
      next = existing.filter((b) => b.effect.kind !== "power-bonus");
      break;
    case "remove-block-buffs":
      next = existing.filter((b) => b.effect.kind !== "block-bonus");
      break;
    case "remove-keyword": {
      const kw = (row.keyword ?? "").toLowerCase();
      if (!kw) {
        // No keyword chosen → strip ALL gain-* buffs (clean-slate debuff).
        next = existing.filter((b) => !String(b.effect.kind).startsWith("gain-"));
      } else {
        const targetKind = keywordToGainKind(kw);
        next = existing.filter((b) => b.effect.kind !== targetKind);
      }
      break;
    }
    default:
      return;
  }
  result[ownerKey].pendingBuffs = next;
  result.log.push(stripLogLine(row, ctx));
}

// ─── Logging ───────────────────────────────────────────────────────────────

function buffLogLine(row: EffectRow, ctx: EffectCtx): string {
  const owner = row.direction === "buff-self" ? "you" : "opponent";
  const targetLabel = humanTarget(row.target);
  switch (row.effect) {
    case "power":
      return `${ctx.cardName}: ${signed(row.amount, row.direction)} power on ${owner}'s ${targetLabel}`;
    case "block":
      return `${ctx.cardName}: ${signed(row.amount, row.direction)} block on ${owner}'s ${targetLabel}`;
    case "stamina-cost":
      return row.direction === "buff-self"
        ? `${ctx.cardName}: ${owner}'s ${targetLabel} costs ${row.amount} less`
        : `${ctx.cardName}: ${owner}'s ${targetLabel} costs ${row.amount} more`;
    case "add-keyword": {
      const amt = row.keywordAmount;
      const kw = row.keyword ?? "";
      const display = typeof amt === "number" && amt > 0 ? `${kw} ${amt}` : kw;
      return `${ctx.cardName}: ${owner}'s ${targetLabel} gains ${display}`;
    }
    default:
      return `${ctx.cardName}: effect on ${owner}'s ${targetLabel}`;
  }
}

function stripLogLine(row: EffectRow, ctx: EffectCtx): string {
  const owner = row.direction === "buff-self" ? "you" : "opponent";
  switch (row.effect) {
    case "remove-power-buffs":
      return `${ctx.cardName}: removed ${owner}'s queued power buffs`;
    case "remove-block-buffs":
      return `${ctx.cardName}: removed ${owner}'s queued block buffs`;
    case "remove-keyword":
      return row.keyword
        ? `${ctx.cardName}: removed ${row.keyword} from ${owner}`
        : `${ctx.cardName}: removed queued keywords from ${owner}`;
    default:
      return `${ctx.cardName}: effect`;
  }
}

function humanTarget(t: EffectTarget): string {
  switch (t) {
    case "my-next-attack":
      return "next attack";
    case "my-next-defend":
      return "next defend";
    case "my-next-action":
      return "next action";
    case "this-attack":
      return "this attack";
    case "this-defend":
      return "this defend";
    case "this-action":
      return "this action";
    case "popup-attack":
      return "live attack";
    case "popup-defend":
      return "live defend";
    case "popup-action":
      return "live action";
    case "self":
      return "yourself";
    case "opponent":
      return "opponent";
    case "my-armor":
      return "your armor";
    case "opp-armor":
      return "opponent's armor";
    case "my-weapons":
      return "your weapon(s)";
    case "opp-weapons":
      return "opponent's weapon(s)";
    case "my-sigs":
      return "your sig moves";
    case "opp-sigs":
      return "opponent's sig moves";
    case "my-champion":
      return "your champion";
    case "opp-champion":
      return "opponent's champion";
  }
}

function signed(n: number | undefined, dir: EffectDirection): string {
  const v = n ?? 0;
  if (dir === "buff-self" || dir === "loadout-self") return `+${v}`;
  return `-${v}`;
}

// ─── Helpers re-exported for the engine slot-mutation path ─────────────────

/** True when the target is a `this-*` (refers to the played-instance copy
 *  itself, edited at play time). */
export function isThisTarget(t: EffectTarget): boolean {
  return t === "this-attack" || t === "this-defend" || t === "this-action";
}

/** True when the target is a `popup-*` (refers to the live combat slot). */
export function isPopupTarget(t: EffectTarget): boolean {
  return t === "popup-attack" || t === "popup-defend" || t === "popup-action";
}

// Re-export EffectKind for type consumers
export type { EffectKind };
